import { Button } from '../components/ui';
import { euro } from '../format';
import { MAX_PARKED } from '../pos/store';
import type { ParkedCart } from '../pos/types';
import { Sheet } from './posParts';

/** Älter als so lange: Hinweis am geparkten Korb (der Kunde ist vermutlich gegangen). */
const PARKED_OLD_MS = 2 * 60 * 60 * 1000;

/** „vor 5 Min“ bzw. „vor 2 Std“ (Alter eines geparkten Korbs). */
export function parkedAge(createdAtMs: number, nowMs: number): string {
  const minutes = Math.max(0, Math.floor((nowMs - createdAtMs) / 60_000));
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${String(minutes)} Min`;
  return `vor ${String(Math.floor(minutes / 60))} Std`;
}

/**
 * Blatt „Parken“ (U17a): den Warenkorb parken und die geparkten Körbe P1 bis P3 fortsetzen. Ersetzt die Leiste der
 * geparkten Körbe im Fuß und das „P“ neben „Kassieren“. Das Blatt zeigt nur an; Parken und Zurückholen laufen weiter über
 * die Warteschlange der Kasse (`requestPark`, `requestUnpark`).
 */
export function ParkSheet({
  cartCount,
  parked,
  nowMs,
  onPark,
  onUnpark,
  onClose,
}: {
  cartCount: number;
  parked: readonly ParkedCart[];
  nowMs: number;
  onPark: () => void;
  onUnpark: (parkNo: number) => void;
  onClose: () => void;
}) {
  const full = parked.length >= MAX_PARKED;
  return (
    <Sheet title="Parken" onClose={onClose}>
      <Button disabled={cartCount === 0 || full} onClick={onPark}>
        {`Warenkorb parken (${String(cartCount)} Artikel)`}
      </Button>
      {full && (
        <p className="muted" role="status">
          Es sind schon {MAX_PARKED} Körbe geparkt. Bitte zuerst einen fortsetzen.
        </p>
      )}
      {parked.length === 0 ? (
        <p className="muted">Kein Korb geparkt.</p>
      ) : (
        <ul className="list" aria-label="Geparkte Körbe">
          {parked.map((cart) => {
            const old = nowMs - cart.createdAtMs > PARKED_OLD_MS;
            const sum = cart.lines.reduce((total, line) => total + line.priceCents, 0);
            return (
              <li key={cart.parkNo} className="cash__parked-row">
                <div className="stack">
                  <strong>{`P${String(cart.parkNo)} · ${String(cart.lines.length)} Artikel · ${euro(sum)}`}</strong>
                  <span className="muted">
                    {parkedAge(cart.createdAtMs, nowMs)}
                    {old ? ' · alt, der Kunde ist vermutlich gegangen' : ''}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  aria-label={`Korb P${String(cart.parkNo)} fortsetzen: ${String(cart.lines.length)} Artikel, ${euro(sum).replace('€', 'Euro')}${old ? ', über 2 Stunden alt' : ''}`}
                  onClick={() => {
                    onUnpark(cart.parkNo);
                  }}
                >
                  Fortsetzen
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {cartCount > 0 && parked.length > 0 && (
        <p className="muted">
          Beim Fortsetzen nimmt der aktuelle Warenkorb den Platz des geholten ein.
        </p>
      )}
    </Sheet>
  );
}
